
import { pricingTiers } from '../../../mocks/pricing';

const PricingSection = () => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="py-32 bg-gradient-to-b from-primary-black to-primary-dark relative overflow-hidden">
      {/* Enhanced background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 50% 30%, rgba(0, 212, 255, 0.18) 0%, transparent 60%),
                           radial-gradient(circle at 20% 70%, rgba(0, 102, 255, 0.12) 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, rgba(0, 212, 255, 0.12) 0%, transparent 50%)`
        }}></div>
        
        {/* Animated particles */}
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full animate-pulse"
            style={{
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              background: `rgba(0, ${180 + Math.random() * 75}, 255, ${0.5 + Math.random() * 0.5})`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
              boxShadow: `0 0 ${8 + Math.random() * 12}px rgba(0, 212, 255, 0.8)`
            }}
          ></div>
        ))}
        
        {/* Glow orbs */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary-blue/10 rounded-full blur-3xl animate-pulse"></div>
        
        {/* Horizontal lines */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-blue/50 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-blue/50 to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-primary-blue"></div>
            <span className="text-primary-blue font-inter text-sm font-semibold tracking-widest uppercase" style={{ textShadow: '0 0 20px rgba(0, 212, 255, 0.8)' }}>
              Packages
            </span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-primary-blue"></div>
          </div>
          <h2 className="text-5xl md:text-6xl font-orbitron font-bold text-white mb-4" style={{ textShadow: '0 0 40px rgba(0, 212, 255, 0.7)' }}>
            CHOOSE YOUR TIER
          </h2>
          <p className="text-gray-400 font-inter text-lg">Premium packages designed for every need</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
          {pricingTiers.map((tier, index) => (
            <div
              key={tier.id}
              className={`relative rounded-3xl p-10 border-2 transition-all duration-500 hover:-translate-y-3 ${
                tier.popular
                  ? 'lg:scale-110 border-primary-blue bg-gradient-to-b from-primary-dark via-primary-black to-primary-dark'
                  : 'border-primary-blue/40 bg-gradient-to-b from-primary-dark/80 to-primary-black/80 backdrop-blur-sm hover:border-primary-blue'
              }`}
              style={{
                boxShadow: tier.popular
                  ? '0 0 60px rgba(0, 212, 255, 0.6), inset 0 0 40px rgba(0, 212, 255, 0.1)'
                  : '0 0 30px rgba(0, 212, 255, 0.25), inset 0 0 20px rgba(0, 212, 255, 0.05)'
              }}
            >
              {/* Glow effect for popular */}
              {tier.popular && (
                <div className="absolute -inset-1 bg-gradient-to-b from-primary-blue/30 to-primary-deepblue/30 rounded-3xl blur-xl"></div>
              )}
              
              {tier.popular && (
                <div 
                  className="absolute -top-4 right-8 bg-gradient-to-r from-primary-blue to-primary-deepblue text-white px-6 py-2 rounded-full font-inter font-bold text-sm" 
                  style={{ boxShadow: '0 0 30px rgba(0, 212, 255, 0.8)' }}
                >
                  MOST POPULAR
                </div>
              )}

              <div className="relative z-10 text-center space-y-6">
                <div className={`inline-block px-8 py-3 rounded-xl font-orbitron font-bold text-lg ${
                  index === 0 ? 'bg-gradient-to-r from-gray-400 to-gray-300 text-primary-black' :
                  index === 1 ? 'bg-gradient-to-r from-yellow-400 to-yellow-300 text-primary-black' :
                  'bg-gradient-to-r from-gray-200 to-white text-primary-black'
                }`} style={{ boxShadow: '0 0 25px rgba(0, 212, 255, 0.5)' }}>
                  {tier.name}
                </div>

                <div className="py-6">
                  <div className="text-6xl font-orbitron font-black text-white" style={{ textShadow: '0 0 40px rgba(0, 212, 255, 0.7)' }}>
                    ${tier.price}
                  </div>
                  <p className="text-gray-400 font-inter text-sm mt-2">per service</p>
                </div>

                <ul className="space-y-4 text-left">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-blue/20 border border-primary-blue/50 flex-shrink-0 mt-0.5" style={{ boxShadow: '0 0 10px rgba(0, 212, 255, 0.4)' }}>
                        <i className="ri-check-line text-primary-blue text-sm"></i>
                      </div>
                      <span className="text-gray-300 font-inter text-sm leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => scrollToSection('contact')}
                  className={`group relative w-full py-4 rounded-xl font-inter font-bold text-lg transition-all duration-500 hover:scale-105 whitespace-nowrap cursor-pointer overflow-hidden ${
                    tier.popular
                      ? 'bg-gradient-to-r from-primary-blue to-primary-deepblue text-white'
                      : 'bg-transparent border-2 border-primary-blue text-primary-blue hover:text-white'
                  }`}
                  style={{ boxShadow: '0 0 30px rgba(0, 212, 255, 0.5)' }}
                >
                  <span className="relative z-10">SELECT {tier.name}</span>
                  {!tier.popular && (
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-blue to-primary-deepblue opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
